import React, {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { useA11y } from 'public/components/online_ordering/useA11y'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  ItemWithTimeBasedRule,
  useTimeBasedRules
} from 'public/components/online_ordering/TimeBasedRuleContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import {
  ModalCloseButton,
  useModalContext
} from 'shared/components/common/modal'
import Image from 'shared/components/common/Image'
import { formatImageURL } from 'src/lib/js/utilities'
import {
  OfferBadge,
  TimeBasedRuleItemModalTag
} from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import Skeleton from 'react-loading-skeleton'
import {
  ModifierGroup,
  SelectionModifierGroup
} from 'public/components/online_ordering/types'
import SelectionList from 'public/components/default_template/online_ordering/item_modal/SelectionList'
import { Channel } from 'public/js/siteUtilities'
import { t } from 'src/translations'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import {
  AlertModalSource,
  useAlertModalContext
} from 'shared/components/common/alert_modal/AlertModal'
import {
  Allergen,
  DiningOptionBehavior,
  // eslint-disable-next-line camelcase
  Menus_MenuItemTag,
  RankedPromoOfferDiscount
} from 'src/apollo/onlineOrdering'
import { getOfferPrice } from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'
import { REQUEST_FAILURE_MESSAGE } from 'shared/js/constants'
import { reportError } from 'src/lib/js/clientError'
import FormattedPrice from 'shared/components/common/price/FormattedPrice'
import QuantitySelector from 'public/components/default_template/online_ordering/item_modal/QuantitySelector'
import {
  MAX_UPSELL_MODAL_VIEWS,
  useUpsell
} from 'public/components/online_ordering/UpsellContext'
import { EditSource, ItemSource } from '../cart/cartUtils'
import {
  ArrowBackIcon,
  InfoIcon,
  AllergenIcon,
  ChevronRightIcon
} from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import useInView from 'src/lib/js/hooks/useInView'
import { useAddToCartHandler } from '../cart/useAddToCartHandler'
import { usePopoverContext } from 'src/shared/components/common/popover/PopoverContext'
import { CART_POPOVER_CONTEXT_KEY } from '../cart/CartModal'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { useAllergenSheet } from './AllergenSheetContext'
import { AllergenWithSource } from './useAllergensWithSource'
import { BrandedButton } from 'src/shared/components/common/button'
import { ButtonType } from 'src/apollo/sites'

type ModalActionsProps = {
  loading?: boolean
  isReorder?: boolean
  selectionGuid?: string | null
  itemGuid?: string | null
  channelGuid?: string
  offer?: RankedPromoOfferDiscount
  itemSource?: ItemSource
  onSuccessfulAddToCart?: () => void
}

const hiddenBehindZoomProps = (
  hidden: boolean
): React.HTMLAttributes<HTMLDivElement> =>
  hidden
    ? ({
        'aria-hidden': true,
        inert: ''
      } as React.HTMLAttributes<HTMLDivElement>)
    : {}

export type BodyProps = {
  name?: string | null
  description?: string | null
  specialRequests?: string | null
  itemGroupGuid?: string | null
  itemMenuGuid?: string | null
  groups?: (ModifierGroup | SelectionModifierGroup)[] | null
  image?: string
  basePrice?: number | null
  // eslint-disable-next-line camelcase
  itemTags?: Menus_MenuItemTag[]
  allergens?: (Allergen | null)[] | null
  allergensWithSource?: AllergenWithSource[]
  allergenSheetOpen?: boolean
  calories?: number | null
} & ModalActionsProps

export const headerId = 'menu-item-modal-header'

export const ItemModalRootContent = forwardRef(
  (
    {
      name,
      description,
      basePrice,
      image,
      specialRequests,
      loading,
      itemGuid,
      itemGroupGuid,
      itemMenuGuid,
      groups,
      itemTags,
      isReorder,
      selectionGuid,
      channelGuid,
      offer,
      allergensWithSource = [],
      calories,
      itemSource,
      onSuccessfulAddToCart,
      allergenSheetOpen: allergenSheetOpenProp = false
    }: BodyProps,
    ref: RefObject<HTMLDivElement>
  ) => {
    const {
      displayedModifier: modifier,
      price,
      quantity,
      setQuantity,
      addSpecialRequests
    } = useModifierContext()
    const { ooRestaurant } = useRestaurant()
    const { channel, loadingCart } = useCart()
    const { validateItemFulfillmentTime, timeBasedRulesMap } =
      useTimeBasedRules()
    const { getOffersTextForEntity } = useOffers()
    const [zoomImage, setZoomImage] = useState(false)
    const headerRef = useRef<HTMLHeadingElement>(null)
    const allergenButtonRef = useRef<HTMLButtonElement>(null)
    const imageWrapperRef = useRef<HTMLDivElement>(null)
    const zoomBackButtonRef = useRef<HTMLButtonElement>(null)
    const wasZoomed = useRef(false)
    const [pinHeader, setPinHeader] = useState(false)
    const inlineHeaderVisible = useInView(headerRef, { root: ref.current })

    // State for item description truncation
    const [isExpanded, setIsExpanded] = useState(false)
    const allergenSheetOpen = allergenSheetOpenProp
    const openAllergenSheet = useAllergenSheet()
    const isMobile = useIsMobile(ScreenWidth.SMALL)

    // Estimated characters that fit in 3 lines based on screen size
    const MAX_CHARS = isMobile ? 180 : 250
    const truncatedText = useMemo(() => {
      if (!description || description.length <= MAX_CHARS) return null

      // Find last space before MAX_CHARS to avoid cutting mid-word
      const cutoff = description.lastIndexOf(' ', MAX_CHARS)
      return description.slice(0, cutoff > 0 ? cutoff : MAX_CHARS)
    }, [description, MAX_CHARS])

    useEffect(() => {
      if (modifier?.isRoot) setPinHeader(!inlineHeaderVisible)
    }, [inlineHeaderVisible, modifier?.isRoot])
    useEffect(() => {
      if (zoomImage) {
        zoomBackButtonRef.current?.focus()
      } else if (wasZoomed.current) {
        imageWrapperRef.current?.focus()
      }
      wasZoomed.current = zoomImage
    }, [zoomImage])

    const timeBasedRules = itemGuid && timeBasedRulesMap[itemGuid]
    const fulfillmentTimeValid = validateItemFulfillmentTime(itemGuid)
    const offersText = getOffersTextForEntity(
      itemGuid,
      itemGroupGuid,
      itemMenuGuid
    )

    const totalItemPrice = useMemo(() => quantity * price, [quantity, price])
    const hasModifiers = groups && groups.length > 0
    const showBasePrice = useMemo(
      () => basePrice !== undefined && basePrice !== null,
      [basePrice]
    )
    const showCalories = useMemo(
      () => calories !== undefined && calories !== null,
      [calories]
    )

    return (
      <>
        {loading ? (
          <ItemLoadingSkeleton />
        ) : (
          <>
            {zoomImage && image && (
              <div
                className='zoomImageContainer'
                data-testid='item-modal-zoom-view'
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation()
                    setZoomImage(false)
                  }
                }}
              >
                <h2 className='pinnedHeader pinned'>
                  <span className='headerText'>{name}</span>
                </h2>
                <button
                  ref={zoomBackButtonRef}
                  type='button'
                  className='itemModalBackButton'
                  aria-label={t('item-modal.back')}
                  onClick={() => setZoomImage(false)}
                >
                  <ArrowBackIcon size='md' accessibility='decorative' />
                </button>
                <div className='zoomImageWrapper'>
                  <Image
                    alt=''
                    src={formatImageURL(image)}
                    className='zoomedImage'
                    eagerLoad
                  />
                </div>
              </div>
            )}
            <div
              className='rootContent'
              ref={ref}
              id='item-modal-root-content'
              data-testid='item-modal-root-content'
              {...hiddenBehindZoomProps(zoomImage)}
            >
              <h2
                className={classnames('pinnedHeader', {
                  pinned: pinHeader || !image
                })}
                aria-hidden='true'
              >
                <span className='headerText'>{pinHeader && name}</span>
                <ModalCloseButton
                  className='itemModalCloseButton'
                  tabIndex={-1}
                />
              </h2>
              <ModalCloseButton className='itemModalCloseButton' />
              {image ? (
                <div
                  ref={imageWrapperRef}
                  className={classnames('imageWrapper', {
                    noMods: !hasModifiers
                  })}
                  data-testid='item-modal-image-wrapper'
                  role='button'
                  tabIndex={0}
                  aria-label={t('item-modal.enlarge-image')}
                  onClick={() => setZoomImage(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setZoomImage(true)
                    }
                  }}
                >
                  <Image
                    alt=''
                    src={formatImageURL(image)}
                    className={classnames('itemModalImage', {
                      noMods: !hasModifiers
                    })}
                    data-testid='item-modal-image'
                    eagerLoad
                  />
                </div>
              ) : null}
              <div
                className={classnames('itemInfo', { extraTopPadding: !image })}
              >
                {timeBasedRules && (
                  <TimeBasedRuleItemModalTag
                    timeBasedRules={timeBasedRules}
                    fulfillmentTimeValid={fulfillmentTimeValid}
                    testId={'time-based-ordering-item-tag-' + itemGuid}
                  />
                )}
                {itemTags && itemTags.length > 0 && (
                  <div
                    className='itemTagsModalContainer'
                    data-testid='menu-item-tags'
                  >
                    {itemTags.map((tag) => (
                      <div
                        key={tag.guid}
                        className='modalItemTag'
                        data-testid='item-tag'
                      >
                        {tag.name}
                      </div>
                    ))}
                  </div>
                )}
                {(name || showBasePrice || showCalories) && (
                  <div className='nameAndPrice'>
                    {name && (
                      <h2
                        className='modalTitle'
                        id={headerId}
                        ref={headerRef}
                        tabIndex={-1}
                      >
                        <span className='headerText'>{name}</span>
                      </h2>
                    )}
                    {(showBasePrice || showCalories) && (
                      <div className='priceAndCals'>
                        {showBasePrice && (
                          <FormattedPrice value={basePrice!} trailingZeros />
                        )}
                        {showBasePrice && showCalories && (
                          <span aria-hidden={true}>•</span>
                        )}
                        {showCalories && (
                          <div className='calories'>
                            {t('item-modal.calories', { calories })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {description && (
                  <p className='description'>
                    {isExpanded || truncatedText === null ? (
                      <>
                        {description}
                        {truncatedText !== null && isExpanded && (
                          <>
                            <button
                              className='readMoreButton'
                              onClick={() => setIsExpanded(false)}
                              type='button'
                            >
                              {t('item-modal.read-less-button')}
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {truncatedText}...
                        <button
                          className='readMoreButton'
                          onClick={() => setIsExpanded(true)}
                          type='button'
                        >
                          {t('item-modal.read-more-button')}
                        </button>
                      </>
                    )}
                  </p>
                )}
                {offersText && offersText.length > 0 && (
                  <div className='offersContainer'>
                    {offersText.map((offer) => (
                      <OfferBadge key={offer} text={offer.toUpperCase()} />
                    ))}
                  </div>
                )}
              </div>
              {allergensWithSource.length > 0 && (
                <button
                  ref={allergenButtonRef}
                  type='button'
                  className='allergens allergensButton'
                  onClick={() => openAllergenSheet?.()}
                  aria-expanded={allergenSheetOpen}
                  aria-haspopup='dialog'
                >
                  <AllergenIcon accessibility='decorative' />
                  <div className='allergensContent'>
                    <div className='allergensTitle'>{t('allergens.title')}</div>
                    <div className='allergensList'>
                      {allergensWithSource.map(({ allergen }) => (
                        <div key={allergen.guid} className='allergenItem'>
                          {t(`allergens.${allergen.code}`, allergen.code)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <ChevronRightIcon accessibility='decorative' />
                </button>
              )}
              <div className='modSections'>
                {groups && groups.length > 0 && (
                  <>
                    <h3 className='modSectionHeader'>
                      {t('item-modal.customize-your-item')}
                    </h3>
                    {groups.map((group: ModifierGroup) => (
                      <SelectionList key={group.guid} group={group} />
                    ))}
                  </>
                )}
                {ooRestaurant?.specialRequestsConfig.enabled &&
                channel !== Channel.ECOMM ? (
                  <div className='specialRequests modSection'>
                    <label htmlFor='items-modal-special-request'>
                      {t('item-modal.special-instructions')}
                    </label>
                    <div className='row textarea'>
                      <textarea
                        id='items-modal-special-request'
                        onChange={(e) => {
                          addSpecialRequests(e.target.value)
                        }}
                        disabled={loading}
                        placeholder={t(
                          'item-modal.special-instructions-placeholder'
                        )}
                        aria-placeholder={t(
                          'item-modal.special-instructions-placeholder'
                        )}
                        defaultValue={specialRequests ?? undefined}
                        maxLength={200}
                        rows={4}
                      />
                    </div>
                    {ooRestaurant.specialRequestsConfig.placeholderMessage && (
                      <div className='specialRequestsMessaging'>
                        <InfoIcon accessibility='decorative' />
                        <div className='messaging'>
                          <div className='advisory'>
                            {t('item-modal.special-instructions-advisory')}
                          </div>
                          <div>
                            {
                              ooRestaurant.specialRequestsConfig
                                .placeholderMessage
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
                <div className='quantitySection modSection'>
                  <div className='quantitySelectorContent'>
                    <div className='quantityLabel'>
                      <label>{t('item-modal.quantity')}</label>
                      {modifier?.usesFractionalQuantity && (
                        <i>
                          <FormattedPrice
                            value={modifier.price ?? 0}
                            trailingZeros={true}
                          />
                          {`/${modifier.unitOfMeasure?.toLowerCase()}`}
                        </i>
                      )}
                    </div>
                    <QuantitySelector
                      min={modifier?.usesFractionalQuantity ? 0 : 1}
                      canIncrease={true}
                      className='ooQuantitySelector rootModal'
                      disabled={loading || loadingCart}
                      defaultValue={quantity}
                      onQuantityChange={setQuantity}
                      totalItemPrice={totalItemPrice}
                      usesFractionalQuantity={
                        !!modifier?.usesFractionalQuantity
                      }
                    />
                  </div>
                  {modifier?.usesFractionalQuantity && (
                    <div className='quantityMessaging'>
                      <InfoIcon accessibility='decorative' />
                      <div className='messaging'>
                        <div className='advisory'>
                          {t('item-modal.weight-advisory')}
                        </div>
                        <div>{t('item-modal.weight-messaging')}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ModalActions
              loading={loading}
              isReorder={isReorder}
              selectionGuid={selectionGuid}
              offer={offer}
              channelGuid={channelGuid}
              itemSource={itemSource}
              onSuccessfulAddToCart={onSuccessfulAddToCart}
              hiddenBehindZoom={zoomImage}
            />
          </>
        )}
      </>
    )
  }
)

const ModalActions = ({
  loading,
  isReorder,
  selectionGuid,
  offer,
  channelGuid,
  itemSource,
  onSuccessfulAddToCart,
  hiddenBehindZoom = false
}: ModalActionsProps & { hiddenBehindZoom?: boolean }) => {
  const context = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const { triggerUpsellModal, selectedUpsellItem, views } = useUpsell()
  const {
    displayedModifier,
    price,
    quantity,
    validate,
    invalidRequiredGroups
  } = useModifierContext()
  const { orderingDisabled } = useRestaurant()
  const { editCartItem, loadingCart, applyPromoCode, channel } = useCart()
  const {
    timeBasedRulesMap,
    verifyNoConflictingItemsInCart,
    verifyItemIsFulfillable,
    validateItemFulfillmentTime
  } = useTimeBasedRules()
  const {
    fulfillmentData,
    guestSelectedFulfillmentTime,
    selectedDiningOptionBehavior,
    diningOptionBehaviorAvailability,
    setFulfillmentTimeModalOpen,
    setForceFulfillmentSelection
  } = useFulfillment()
  const { onClose } = useModalContext()
  const { openAlertModal } = useAlertModalContext()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const isDisabled = loading || !quantity || loadingCart
  const scheduledOrdersOnly = useMemo(() => {
    const behavior =
      diningOptionBehaviorAvailability[
        selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
          ? 'delivery'
          : 'takeout'
      ]
    return behavior?.scheduledAvailable && !behavior.asapAvailable
  }, [diningOptionBehaviorAvailability, selectedDiningOptionBehavior])

  const itemGuid = displayedModifier?.modifier.itemGuid
  const itemHasTimeBasedRules = itemGuid && timeBasedRulesMap[itemGuid]
  const itemFulfillmentTimeValid = validateItemFulfillmentTime(itemGuid)

  const promptFulfillmentTimeUpdate = useMemo(
    () =>
      (scheduledOrdersOnly && !guestSelectedFulfillmentTime) ||
      (itemHasTimeBasedRules && !itemFulfillmentTimeValid),
    [
      scheduledOrdersOnly,
      guestSelectedFulfillmentTime,
      itemHasTimeBasedRules,
      itemFulfillmentTimeValid
    ]
  )
  const priceAfterOffer = useMemo(
    () => getOfferPrice(offer, price),
    [offer, price]
  )
  const { announce } = useA11y()

  const handleAddToCart = useAddToCartHandler()

  const handleAddToCartFailure = useCallback(
    (error: any) => {
      openAlertModal(
        REQUEST_FAILURE_MESSAGE,
        undefined,
        AlertModalSource.ITEM_MODAL
      )
      reportError('Error adding cart item.', error)
    },
    [openAlertModal]
  )

  const onSubmit = useCallback(async (): Promise<boolean> => {
    if (!displayedModifier?.isRoot) {
      return false
    }

    if (!validate()) {
      if (invalidRequiredGroups?.length > 0) {
        announce(
          [
            t('item-modal.following-required-mods'),
            ...invalidRequiredGroups.map((group) => group.name)
          ].join(' '),
          'assertive'
        )
      }
      return false
    }

    let errorType = null
    if (selectionGuid && !isReorder) {
      try {
        errorType = await editCartItem(
          displayedModifier,
          quantity,
          selectionGuid,
          itemSource,
          EditSource.ItemModal
        )
      } catch (error) {
        openAlertModal(
          REQUEST_FAILURE_MESSAGE,
          undefined,
          AlertModalSource.ITEM_MODAL
        )
        reportError('Error editing cart item.', error)
      }
    } else {
      const timeBasedRules =
        timeBasedRulesMap[displayedModifier.modifier.itemGuid]

      const item = {
        itemGuid: displayedModifier.modifier.itemGuid,
        preorderRule: timeBasedRules?.preorderRule,
        leadTimeRule: timeBasedRules?.leadTimeRule,
        pickupWindowRule: timeBasedRules?.pickupWindowRule,
        name: displayedModifier.name,
        modifier: displayedModifier,
        quantity
      } as ItemWithTimeBasedRule
      if (!verifyNoConflictingItemsInCart(item)) {
        onClose()
        return false
      }

      const additionalTrackData = {
        addToCartSource: 'item_modal',
        priceAfterOffer,
        isReorder
      }

      const addToCartResponse = await handleAddToCart(
        displayedModifier,
        quantity,
        channelGuid || '',
        itemSource || ItemSource.Menu,
        handleAddToCartFailure,
        onSuccessfulAddToCart,
        additionalTrackData
      )

      // If this item is being added from a BOGO, add the promo code first
      if (
        addToCartResponse.errorType !== 'CartModificationError' &&
        offer?.promoCode
      ) {
        applyPromoCode(offer.promoCode)
      }
    }
    return errorType !== 'CartModificationError'
  }, [
    displayedModifier,
    validate,
    invalidRequiredGroups,
    announce,
    selectionGuid,
    isReorder,
    editCartItem,
    quantity,
    timeBasedRulesMap,
    verifyNoConflictingItemsInCart,
    priceAfterOffer,
    channelGuid,
    itemSource,
    offer,
    onClose,
    applyPromoCode,
    handleAddToCart,
    onSuccessfulAddToCart,
    handleAddToCartFailure,
    openAlertModal
  ])

  const handleCartMutation = useCallback(async () => {
    const success = await onSubmit()
    if (!success) {
      return
    }

    // Determine which modal (if any) should open after successfully modifying cart
    if (promptFulfillmentTimeUpdate) {
      // Item requires fulfillment time selection - open fulfillment time modal
      setForceFulfillmentSelection(true)
      await setFulfillmentTimeModalOpen(true)
    } else if (selectedUpsellItem === null && views < MAX_UPSELL_MODAL_VIEWS) {
      // No fulfillment update needed and upsell hasn't been shown - show upsell modal
      triggerUpsellModal(quantity)
    } else if (!isMobile || selectionGuid) {
      // Open cart
      context?.open()
    }

    onSuccessfulAddToCart?.()

    // Close the item modal
    onClose()
  }, [
    onSubmit,
    promptFulfillmentTimeUpdate,
    setForceFulfillmentSelection,
    setFulfillmentTimeModalOpen,
    selectedUpsellItem,
    views,
    triggerUpsellModal,
    quantity,
    onClose,
    onSuccessfulAddToCart,
    isMobile,
    context,
    selectionGuid
  ])

  const totalItemPrice = useMemo(() => quantity * price, [quantity, price])
  const formattedPrice = useMemo(() => {
    if (offer?.estimatedSavings !== undefined) {
      return (
        <>
          <span className='discountedPrice'>
            <FormattedPrice value={totalItemPrice} />
          </span>{' '}
          {priceAfterOffer === 0 ? (
            t('item-modal.free')
          ) : (
            <FormattedPrice value={priceAfterOffer} />
          )}
        </>
      )
    }
    return <FormattedPrice value={totalItemPrice} />
  }, [offer?.estimatedSavings, totalItemPrice, priceAfterOffer])

  const isOrderingDisabled = useMemo(
    () => channel !== Channel.ECOMM && orderingDisabled,
    [channel, orderingDisabled]
  )

  if (isOrderingDisabled) {
    return (
      <div
        className='actionWrapper'
        {...hiddenBehindZoomProps(hiddenBehindZoom)}
      >
        <button type='button' disabled className='modalButton centered'>
          {t('item-modal.currently-not-accepting')}
        </button>
      </div>
    )
  }

  const fulfillmentType = fulfillmentData?.cartFulfillmentData.fulfillmentType
  const futureSchedules = fulfillmentData?.scheduleData.futureScheduleDates
  if (
    displayedModifier?.isRoot &&
    fulfillmentType &&
    !verifyItemIsFulfillable(
      displayedModifier.modifier.itemGuid,
      fulfillmentType,
      futureSchedules ?? []
    )
  ) {
    return (
      <div
        className='actionWrapper'
        {...hiddenBehindZoomProps(hiddenBehindZoom)}
      >
        <button type='button' disabled className='modalButton centered'>
          {t('item-modal.not-currently-available')}
        </button>
      </div>
    )
  }

  return (
    <>
      <div
        className='actionWrapper'
        data-testid='item-modal-action-wrapper'
        {...hiddenBehindZoomProps(hiddenBehindZoom)}
      >
        <BrandedButton
          type='button'
          className='modalButton'
          disabled={isDisabled}
          onClick={handleCartMutation}
          data-testid='menu-item-cart-cta'
          variant={ButtonType.Primary}
        >
          <div>
            {selectionGuid && !isReorder
              ? t('item-modal.update-cart')
              : t('item-modal.add-to-cart')}
          </div>
          {!loading && <div>{isNaN(quantity) ? '0.00' : formattedPrice}</div>}
        </BrandedButton>
      </div>
    </>
  )
}

const ItemLoadingSkeleton = () => {
  return (
    <>
      <div className='rootContent loading'>
        <ModalCloseButton className='itemModalCloseButton' />
        <div className='imageWrapper'>
          <div className='loadingImage' />
        </div>
        <div className='loadingItemInfo'>
          <div className='loadingNameAndPrice'>
            <Skeleton height='24px' width='70%' />
            <Skeleton height='24px' width='20%' />
          </div>
          <div className='loadingDescription'>
            <Skeleton height='16px' width='96%' />
            <Skeleton height='16px' width='90%' />
            <Skeleton height='16px' width='30%' />
          </div>
        </div>
        <div className='loadingModSections'>
          <div className='loadingModSection'>
            <div className='loadingModSectionTitle' />
            <div className='loadingModSectionContent' />
          </div>
          <div className='loadingModSection'>
            <div className='loadingModSectionTitle' />
            <div className='loadingModSectionContent' />
          </div>
          <div className='loadingModSection'>
            <div className='loadingModSectionTitle' />
            <div className='loadingModSectionContent' />
          </div>
        </div>
      </div>
      <div className='actionWrapper'>
        <div className='loadingButton' />
      </div>
    </>
  )
}
